import { useState } from "react";
import { ChevronLeft, ChevronRight, Settings, Lock, ChevronDown } from "lucide-react";
import type { MonthlyBillingReport, BillingProcess } from "../types";
import { formatFcfa, formatNumber, getMonthLabel } from "../lib/utils";

function ProcessRow({
  process,
  index,
  isAdmin,
  onEdit,
  onViewDetails,
  expandedProcessId,
}: {
  process: MonthlyBillingReport["processes"][0];
  index: number;
  isAdmin: boolean;
  onEdit?: () => void;
  onViewDetails?: (process: MonthlyBillingReport["processes"][0]) => void;
  expandedProcessId: number | null;
}) {
  const hasData = process.monthlyTotal > 0;
  const isExpanded = expandedProcessId === process.processId;

  return (
    <>
      <tr className={hasData ? "" : "empty-row"}>
        <td className="col-index">{index + 1}</td>
        <td className="col-process">
          <button
            className="process-name-btn"
            onClick={() => onViewDetails?.(process)}
            title="Cliquer pour voir les détails quotidiens"
          >
            <ChevronDown size={16} className={`chevron ${isExpanded ? "expanded" : ""}`} />
            <div className="process-info">
              <span className="process-title">{process.processTitle}</span>
              <span className={`pickup-badge ${process.pickupEvent.toLowerCase()}`}>
                {process.pickupEvent === "Collecté" ? "Événement : Collecté" : "Événement : Création"}
              </span>
            </div>
          </button>
        </td>
        <td className="col-amount">{formatFcfa(process.amountFcfa)}</td>
        <td className="col-total">{formatNumber(process.monthlyTotal)}</td>
        {isAdmin && (
          <td className="col-actions">
            <button className="icon-btn" onClick={onEdit} title="Modifier">
              <Settings size={16} />
            </button>
          </td>
        )}
      </tr>
      {isExpanded && (
        <tr className="details-row">
          <td colSpan={isAdmin ? 5 : 4}>
            <DailyCountsTable process={process} />
          </td>
        </tr>
      )}
    </>
  );
}

function DailyCountsTable({ process }: { process: MonthlyBillingReport["processes"][0] }) {
  if (process.dailyCounts.length === 0) {
    return <p className="no-data">Aucune donnée pour ce processus ce mois-ci</p>;
  }

  return (
    <div className="daily-counts-table-wrapper">
      <table className="daily-counts-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Collectes</th>
          </tr>
        </thead>
        <tbody>
          {process.dailyCounts.map((day) => (
            <tr key={day.date}>
              <td>{day.date}</td>
              <td className="count">{formatNumber(day.count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BillingView({
  report,
  isAdmin,
  selectedMonth,
  onMonthChange,
  onEditProcess,
}: {
  report: MonthlyBillingReport;
  isAdmin: boolean;
  selectedMonth: { year: number; month: number };
  onMonthChange: (month: { year: number; month: number }) => void;
  onEditProcess?: (process: BillingProcess) => void;
}) {
  const monthLabel = getMonthLabel(selectedMonth.year, selectedMonth.month);
  const [expandedProcessId, setExpandedProcessId] = useState<number | null>(null);

  // Sort processes by amount (largest first)
  const sortedProcesses = [...report.processes].sort((a, b) => b.amountFcfa - a.amountFcfa);

  const handleViewDetails = (process: MonthlyBillingReport["processes"][0]) => {
    setExpandedProcessId(expandedProcessId === process.processId ? null : process.processId);
  };

  return (
    <div className="billing-view">
      <header className="view-header">
        <div className="header-left">
          <h1>Facturation SPT</h1>
          <span className="month-badge month-display">{monthLabel}</span>
        </div>
        <div className="header-right">
          <div className="month-nav">
            <button className="icon-btn" onClick={() => onMonthChange(getPrevMonth(selectedMonth))} aria-label="Mois précédent">
              <ChevronLeft size={20} />
            </button>
            <select
              value={`${selectedMonth.year}-${String(selectedMonth.month).padStart(2, "0")}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                onMonthChange({ year: y, month: m });
              }}
              aria-label="Sélectionner un mois"
            >
              {report.availableMonths.map((m) => (
                <option key={`${m.year}-${m.month}`} value={`${m.year}-${String(m.month).padStart(2, "0")}`}>
                  {m.label}
                </option>
              ))}
            </select>
            <button className="icon-btn" onClick={() => onMonthChange(getNextMonth(selectedMonth))} aria-label="Mois suivant">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="config-summary">
        <div className="config-item">
          <span className="config-label">Forfait fixe</span>
          <span className="config-value">{formatFcfa(report.config.fixedBaseFcfa)}</span>
        </div>
        <div className="config-item">
          <span className="config-label">Prix par collecte</span>
          <span className="config-value">{formatFcfa(report.config.pricePerPickupFcfa)}</span>
        </div>
        <div className="config-item total">
          <span className="config-label">Total du mois</span>
          <span className="config-value">{formatFcfa(report.grandTotalFcfa)}</span>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="billing-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Processus / Application</th>
              <th>Montant (FCFA)</th>
              <th>Collectes du mois</th>
              {isAdmin && <th className="col-actions-header">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedProcesses.map((process, index) => (
              <ProcessRow
                key={process.processId}
                process={process}
                index={index}
                isAdmin={isAdmin}
                onEdit={onEditProcess ? () => onEditProcess({
                  processId: process.processId,
                  title: process.processTitle,
                  pickupEvent: process.pickupEvent as "Created" | "Collecté",
                  isActive: true,
                }) : undefined}
                onViewDetails={handleViewDetails}
                expandedProcessId={expandedProcessId}
              />
            ))}
            <tr className="grand-total-row">
              <td colSpan={2} className="total-label">Total général</td>
              <td className="total-amount">{formatFcfa(report.grandTotalFcfa)}</td>
              <td className="total-count">{formatNumber(report.grandTotalPickups)} collectes</td>
              {isAdmin && <td />}
            </tr>
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <details className="daily-details-section">
          <summary>Détails quotidiens par processus</summary>
          <div className="daily-details-grid">
            {report.processes.map((process) => (
              <div key={process.processId} className="daily-detail-card">
                <h4>{process.processTitle}</h4>
                <DailyCountsTable process={process} />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function getPrevMonth({ year, month }: { year: number; month: number }) {
  if (month === 1) return { year: year - 1, month: 12 };
  return { year, month: month - 1 };
}

function getNextMonth({ year, month }: { year: number; month: number }) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (month === 12) return { year: year + 1, month: 1 };
  if (year === currentYear && month === currentMonth) return { year, month };
  return { year, month: month + 1 };
}

export function AuthScreen({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="shell center-screen">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{title}</h1>
        </div>
        <div className="auth-content">{children}</div>
      </div>
    </div>
  );
}

export function AdminLoginForm({ onLogin }: { onLogin: (password: string) => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === "spt-admin-2026") {
      onLogin(password);
    } else {
      setError("Mot de passe incorrect");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="auth-error">{error}</div>}
      <div className="form-group">
        <label htmlFor="admin-password">Mot de passe administrateur</label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Entrez le mot de passe"
          autoFocus
        />
      </div>
      <button type="submit" className="btn btn-primary btn-block">
        <Lock size={18} /> Accéder à l'administration
      </button>
    </form>
  );
}